import confetti from "canvas-confetti";

import { haptics } from "./haptics";

/**
 * Fire a warm-palette confetti burst as a per-trip milestone celebration.
 *
 * Guarded on `prefers-reduced-motion` — reduced-motion users still get
 * the haptic and any visual state change from the caller, but no
 * canvas burst. Also idempotent per `key`: once fired for a trip in a
 * session, subsequent calls no-op. The store lives in sessionStorage
 * so a page reload gives the user a fresh chance to see it (they
 * probably didn't the first time; the tab was in the background).
 */
export function celebrate(key: string, opts: { pieces?: number; durationMs?: number } = {}): void {
  if (typeof window === "undefined") return;
  const flag = `celebrated:${key}`;
  try {
    if (window.sessionStorage.getItem(flag)) return;
    window.sessionStorage.setItem(flag, "1");
  } catch {
    /* private mode / disabled — just fire once per app load */
  }

  haptics.success();

  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return;

  const pieces = opts.pieces ?? 32;
  const duration = opts.durationMs ?? 2200;
  const end = Date.now() + duration;

  // Warm palette pulled from design-system §2.3 — coral, terracotta,
  // amber, forest. No cool blues.
  const colors = ["#E4572E", "#D08A3F", "#B87E28", "#3F7A5E", "#F4C89D"];

  (function frame() {
    confetti({
      particleCount: Math.max(3, Math.round(pieces / 8)),
      angle: 60,
      spread: 70,
      origin: { x: 0, y: 0.65 },
      colors,
      scalar: 0.9,
      gravity: 1,
      ticks: 240,
    });
    confetti({
      particleCount: Math.max(3, Math.round(pieces / 8)),
      angle: 120,
      spread: 70,
      origin: { x: 1, y: 0.65 },
      colors,
      scalar: 0.9,
      gravity: 1,
      ticks: 240,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}
